import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DOMPurify from 'dompurify';
import md5 from 'md5';
import { Mail, ChevronDown, X, ArrowLeft, Download, FileText, Loader2, Share2, Receipt, MoreHorizontal } from 'lucide-react';
import { EmailReplyComposer } from './EmailReplyComposer';
import UploadReceiptModalWrapper from '../expenses/UploadReceiptModalWrapper';
import { useModalZIndex } from '../../contexts/ModalStackContext';

/** Gravatar URL for an email (d=404 so we can fallback when no image exists) */
function getGravatarUrl(email: string | undefined, size = 80): string | null {
  if (!email || typeof email !== 'string') return null;
  const raw = email.replace(/^.*<([^>]+)>.*$/, '$1').trim().toLowerCase();
  if (!raw || !raw.includes('@')) return null;
  const hash = md5(raw);
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=404`;
}

/** Parse "Display Name <email@x.com>" → { name, email }. Falls back gracefully. */
function parseEmailAddress(raw?: string | null): { name: string; email: string } {
  if (!raw || typeof raw !== 'string') return { name: '', email: '' };
  const m = raw.match(/^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/);
  if (m) {
    const email = m[2].trim().toLowerCase();
    const name = (m[1] || '').trim();
    return { name: name || email, email };
  }
  const email = raw.trim();
  return { name: email, email: email.toLowerCase() };
}

/** Parse a comma/semicolon-separated recipient list into individual addresses. */
function parseAddressList(raw?: string | null): Array<{ name: string; email: string }> {
  if (!raw) return [];
  // Split on commas/semicolons that aren't inside angle brackets or quotes.
  return raw
    .split(/,(?![^<]*>)|;(?![^<]*>)/)
    .map((part) => parseEmailAddress(part))
    .filter((a) => a.email);
}

/** First initial for an avatar, from a display name or email. */
function initialFor(nameOrEmail: string): string {
  const s = (nameOrEmail || '').trim();
  if (!s) return '?';
  const ch = s[0];
  return /[a-z0-9]/i.test(ch) ? ch.toUpperCase() : '#';
}

// Gmail-style deterministic avatar palette (saturated, white text on top).
const AVATAR_COLORS = [
  '#1abc9c', '#16a085', '#2ecc71', '#27ae60', '#3498db', '#2980b9',
  '#9b59b6', '#8e44ad', '#e67e22', '#d35400', '#e74c3c', '#c0392b',
  '#f39c12', '#0ea5e9', '#6366f1', '#ec4899', '#14b8a6', '#ef4444',
];
/** Stable color for an email/name so the same sender always gets the same circle. */
function avatarColor(key: string): string {
  const s = (key || 'x').toLowerCase();
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/**
 * Split a sanitized HTML body into the new content vs the trailing quoted history,
 * so the UI can hide the quote behind a Gmail-style "•••" toggle (without deleting it).
 */
function splitQuotedHtml(rawHtml: string): { visible: string; quoted: string } {
  if (!rawHtml || typeof window === 'undefined' || !window.DOMParser) {
    return { visible: rawHtml, quoted: '' };
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, 'text/html');
    const body = doc.body;
    if (!body) return { visible: rawHtml, quoted: '' };

    const selectors = [
      '.gmail_quote', '.gmail_quote_container', 'blockquote', '.yahoo_quoted',
      '.outlook_quoted', '.hotmail_quote', '.moz-cite-prefix', 'div[type="cite"]', '[data-gmail-quote]'
    ];
    let quote: Element | null = null;
    for (const sel of selectors) {
      const found = body.querySelector(sel);
      if (found) { quote = found; break; }
    }
    if (!quote) return { visible: body.innerHTML, quoted: '' };

    // Walk up so we treat the top-level container (and everything after it) as quoted.
    let top: Element = quote;
    while (top.parentElement && top.parentElement !== body) top = top.parentElement;

    const quotedParts: string[] = [];
    let cur: Element | null = top;
    while (cur) {
      const next = cur.nextElementSibling;
      quotedParts.push(cur.outerHTML);
      cur.remove();
      cur = next;
    }

    const visible = body.innerHTML.trim();
    const quoted = quotedParts.join('').trim();
    // If there's no real new content, don't hide everything — show it all.
    if (!visible) return { visible: quoted, quoted: '' };
    return { visible, quoted };
  } catch {
    return { visible: rawHtml, quoted: '' };
  }
}

/** Wrap sanitized body HTML in a minimal, isolated email document for an iframe.
 *  overflow:hidden so CapGo/WKWebView doesn't create a nested UIScrollView that
 *  fights the parent modal scroller (mid-gesture "stuck" scroll). */
function wrapEmailDoc(bodyHtml: string): string {
  const baseStyles = `
    html, body { margin: 0; padding: 0; background: #ffffff; color: #1f2937; height: auto !important; overflow: hidden !important; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; line-height: 1.6; }
    .email-wrapper { width: 100%; margin: 0; padding: 0; box-sizing: border-box; overflow-x: hidden; }
    img { max-width: 100% !important; height: auto !important; }
    table { max-width: 100% !important; width: auto !important; }
    td, th { word-break: normal; }
  `;
  return `<!doctype html><html><head><meta charset="utf-8" /><style>${baseStyles}</style></head><body><div class="email-wrapper">${bodyHtml}</div></body></html>`;
}

/** Measure iframe content height and remeasure when images/layout change. */
function measureIframeContentHeight(iframe: HTMLIFrameElement | null): number {
  if (!iframe) return 0;
  try {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return 0;
    return Math.max(doc.body?.scrollHeight || 0, doc.documentElement?.scrollHeight || 0);
  } catch {
    return 0;
  }
}

function observeIframeHeight(
  iframe: HTMLIFrameElement | null,
  onHeight: (height: number) => void
): () => void {
  if (!iframe) return () => {};
  const update = () => {
    const h = measureIframeContentHeight(iframe);
    if (h > 0) onHeight(h);
  };
  update();

  let ro: ResizeObserver | null = null;
  const imgCleanups: Array<() => void> = [];
  try {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) {
      if (typeof ResizeObserver !== 'undefined' && doc.body) {
        ro = new ResizeObserver(() => update());
        ro.observe(doc.body);
      }
      doc.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        const onLoad = () => update();
        img.addEventListener('load', onLoad);
        img.addEventListener('error', onLoad);
        imgCleanups.push(() => {
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onLoad);
        });
      });
    }
  } catch {
    /* ignore */
  }

  const t1 = window.setTimeout(update, 200);
  const t2 = window.setTimeout(update, 700);
  return () => {
    ro?.disconnect();
    imgCleanups.forEach((fn) => fn());
    window.clearTimeout(t1);
    window.clearTimeout(t2);
  };
}

/**
 * Renders one message's body Gmail-style. Rich/marketing HTML (tables, <style>, large
 * docs) renders in an isolated iframe; simple reply HTML/plain text renders inline with
 * the trailing quoted history hidden behind a "•••" toggle. Module-scoped so it keeps a
 * stable identity across parent re-renders (no iframe reload flicker when toggling).
 */
function MessageBody({
  sanitizedHtml,
  isFancy,
  showQuoted,
  onToggleQuoted,
  interceptLinks,
  onContentClick
}: {
  sanitizedHtml: string;
  isFancy: boolean;
  showQuoted: boolean;
  onToggleQuoted: () => void;
  interceptLinks: (iframe: HTMLIFrameElement | null) => void;
  onContentClick: (e: React.MouseEvent) => void;
}) {
  const localIframeRef = useRef<HTMLIFrameElement>(null);
  const [frameHeight, setFrameHeight] = useState(0);
  const [iframeLoadGen, setIframeLoadGen] = useState(0);
  const srcDoc = useMemo(() => (isFancy ? wrapEmailDoc(sanitizedHtml) : ''), [isFancy, sanitizedHtml]);
  const { visible, quoted } = useMemo(
    () => (isFancy ? { visible: '', quoted: '' } : splitQuotedHtml(sanitizedHtml)),
    [isFancy, sanitizedHtml]
  );

  useEffect(() => {
    if (!isFancy) return;
    setFrameHeight(0);
  }, [isFancy, sanitizedHtml]);

  useEffect(() => {
    if (!isFancy || iframeLoadGen === 0) return;
    return observeIframeHeight(localIframeRef.current, setFrameHeight);
  }, [isFancy, iframeLoadGen, srcDoc]);

  if (isFancy) {
    return (
      <iframe
        ref={localIframeRef}
        title="Email message"
        className="w-full border-0"
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        scrolling="no"
        srcDoc={srcDoc}
        onLoad={() => {
          interceptLinks(localIframeRef.current);
          setIframeLoadGen((n) => n + 1);
        }}
        style={{
          minHeight: 80,
          height: frameHeight ? `${frameHeight}px` : 'auto',
          overflow: 'hidden',
        }}
      />
    );
  }

  return (
    <div className="text-sm text-gray-800" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', lineHeight: 1.6 }}>
      <div dangerouslySetInnerHTML={{ __html: visible }} onClick={onContentClick} />
      {quoted && (
        <div className="mt-1.5">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleQuoted(); }}
            className="inline-flex items-center justify-center h-6 px-2.5 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-500 transition-colors touch-manipulation"
            title={showQuoted ? 'Hide quoted text' : 'Show quoted text'}
            aria-label={showQuoted ? 'Hide quoted text' : 'Show quoted text'}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
          {showQuoted && (
            <div
              className="mt-2 border-l-2 border-gray-200 pl-3 text-gray-500"
              dangerouslySetInnerHTML={{ __html: quoted }}
              onClick={onContentClick}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Avatar: Gravatar image if available, else a deterministic Gmail-style colored initial. */
function SenderAvatar({
  email,
  name,
  isOutbound,
  size = 40
}: {
  email?: string;
  name?: string;
  isOutbound: boolean;
  size?: number;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const url = getGravatarUrl(email, 160);
  const showImg = url && !imgFailed;
  const label = name || email || '';
  const initial = initialFor(label);
  const bg = isOutbound ? '#5f6368' : avatarColor(email || label);
  const dim = { width: size, height: size };
  if (showImg) {
    return (
      <img
        src={url}
        alt=""
        style={dim}
        className="rounded-full object-cover flex-shrink-0"
        onError={() => setImgFailed(true)}
      />
    );
  }
  return (
    <div
      className="flex-shrink-0 rounded-full flex items-center justify-center text-white font-medium select-none"
      style={{ ...dim, backgroundColor: bg, fontSize: Math.round(size * 0.42) }}
      aria-hidden
    >
      {initial}
    </div>
  );
}

interface EmailDisplayProps {
  content: string;
  subject?: string;
  createdAt?: string;
  className?: string;
  isOutbound?: boolean;
  compactView?: boolean;
  replyTo?: string;
  replySubject?: string;
  businessId?: string;
  conversationId?: string;
  customerId?: string | null;
  onSendSuccess?: (data: any) => void;
  onSendError?: (error: any) => void;
  isEmailDisabled?: boolean;
  onReplyClick?: (replyTo: string, replySubject: string, gmailId?: string) => void;
  /** Reply-all: prefilled primary recipient + Cc list (everyone else, minus us). */
  onReplyAllClick?: (to: string, cc: string, replySubject: string, gmailId?: string) => void;
  /** Forward: prefilled forwarded HTML body + "Fwd:" subject (recipient left blank). */
  onForwardClick?: (forwardedHtml: string, forwardSubject: string) => void;
  /** Gmail message ID of this email (for reply threading - from all_messages.twilio_message_sid or external_id) */
  replyToGmailId?: string | null;
  inlineAttachments?: Array<{
    content_id?: string | null;
    public_url: string;
  }>;
  attachments?: Array<{
    id: string;
    filename: string | null;
    content_type: string | null;
    size_bytes: number | null;
    public_url: string;
  }>;
  threadMessages?: Array<{
    id: string;
    subject?: string;
    body: string;
    created_at: string;
    direction?: 'inbound' | 'outbound';
    read?: 'read' | 'unread';
    from_number?: string;
    to_number?: string;
    cc?: string | null;
    has_attachments?: boolean;
    twilio_message_sid?: string | null;
  }>;
  /** Current user's email (for "You" avatar Gravatar in thread view) */
  userEmail?: string | null;
  /** When true, open the full-email (thread) modal on mount (e.g. when navigating from search) */
  initialOpenFullEmail?: boolean;
  /** Called once after opening the full email modal via initialOpenFullEmail */
  onFullEmailOpened?: () => void;
  /** For unread badge and Mark as read in the email preview */
  locallyReadMessages?: Set<string>;
  onMarkAsRead?: (messageIds: string[]) => void;
  /** Called when the mobile back arrow is tapped to return to the unread pipeline */
  onBackToUnread?: () => void;
}

/** Strip HTML and get plain-text preview for snippet (e.g. first 60 chars) */
function getBodyPreview(htmlOrText: string, maxLength = 60): string {
  if (!htmlOrText || typeof window === 'undefined') return '';
  let text = htmlOrText;
  if (/<[a-z][\s\S]*>/i.test(text)) {
    try {
      const parser = typeof DOMParser !== 'undefined' ? new DOMParser() : null;
      if (parser) {
        const doc = parser.parseFromString(text, 'text/html');
        text = doc.body?.textContent || text;
      } else {
        text = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      }
    } catch {
      text = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    }
  }
  text = text.replace(/\s+/g, ' ').trim();
  return text.length > maxLength ? text.slice(0, maxLength).trim() + '…' : text;
}

/** Short label for participant (e.g. "josh@swyf.ca" -> "josh" or first 15 chars) */
function shortParticipant(replyTo: string | undefined): string {
  if (!replyTo) return '';
  const match = replyTo.match(/<([^>]+)>/);
  const email = match ? match[1] : replyTo;
  const local = email.split('@')[0] || email;
  return local.length > 12 ? local.slice(0, 12) + '…' : local;
}

/** Format time as "5:18 PM (29 minutes ago)" for recent, else "Feb 17, 2026, 5:18 PM" */
function formatMessageTime(createdAt: string): string {
  const date = new Date(createdAt);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const timeStr = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
  if (diffMins < 1) return `${timeStr} (just now)`;
  if (diffMins < 60) return `${timeStr} (${diffMins} minute${diffMins === 1 ? '' : 's'} ago)`;
  if (diffHours < 24) return `${timeStr} (${diffHours} hour${diffHours === 1 ? '' : 's'} ago)`;
  if (diffDays < 7) return `${timeStr} (${diffDays} day${diffDays === 1 ? '' : 's'} ago)`;
  return date.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });
}

/** Remove quoted reply block so we only show the new reply, not "Re: subject" + date + quoted body. */
function stripQuotedReplyFromBody(html: string, subjectLine?: string): string {
  if (!html || typeof html !== 'string') return html;
  const lower = html.toLowerCase();
  let out = html;

  const hrIndex = lower.indexOf('<hr');
  if (hrIndex !== -1) {
    out = html.slice(0, hrIndex).trim();
    return out;
  }

  if (subjectLine && subjectLine.trim()) {
    const sub = subjectLine.trim();
    const subLower = sub.toLowerCase();
    const idx = lower.indexOf(subLower);
    if (idx > 20) {
      out = html.slice(0, idx).trim();
    }
  }
  return out;
}

// Clean and prepare HTML email content for safe display
function stripQuotedHtml(rawHtml: string): string {
  if (!rawHtml || typeof window === 'undefined' || !window.DOMParser) {
    return rawHtml;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, 'text/html');

    const selectors = [
      'blockquote',
      '.gmail_quote',
      '.yahoo_quoted',
      '.outlook_quoted',
      '.hotmail_quote',
      '.moz-cite-prefix',
      'div[type="cite"]',
      '[data-gmail-quote]'
    ];

    doc.querySelectorAll(selectors.join(',')).forEach((node) => {
      node.remove();
    });

    return doc.body?.innerHTML || rawHtml;
  } catch {
    return rawHtml;
  }
}

function prepareEmailHTML(rawContent: string): string {
  if (!rawContent) return "";

  let content = rawContent;

  // Check upfront whether the raw content is HTML so we can skip
  // plain-text heuristics that destroy HTML structure.
  const isHtml = /<(!doctype|html|head|body|table|style|div|p|span|img|a)\b/i.test(content);

  if (isHtml) {
    // For HTML emails, only do safe transformations:
    // 1. Decode quoted-printable soft line breaks
    content = content.replace(/=\r\n/g, "");
    // 2. Decode quoted-printable hex escapes
    content = content
      .replace(/=3D/g, "=")
      .replace(/=20/g, " ")
      .replace(/=0A/g, "\n")
      .replace(/=0D/g, "\r")
      .replace(/=([0-9A-F]{2})/gi, (_, hex) =>
        String.fromCharCode(parseInt(hex, 16))
      );

    // Use the DOM-aware quoted reply stripper instead of line-based heuristics
    content = stripQuotedHtml(content);
    return content;
  }

  // --- Plain-text email path ---

  // Remove MIME boundaries and headers
  content = content.replace(/--[a-zA-Z0-9_-]+[\s\S]*?--[a-zA-Z0-9_-]+--/g, "");
  content = content.replace(/Content-Type:.*$/gm, "");
  content = content.replace(/Content-Transfer-Encoding:.*$/gm, "");
  content = content.replace(/Content-Disposition:.*$/gm, "");

  // Remove base64 encoded attachments
  content = content.replace(/[A-Za-z0-9+/]{50,}={0,2}/g, "");

  // Decode common email encodings
  content = content
    .replace(/=3D/g, "=")
    .replace(/=20/g, " ")
    .replace(/=0A/g, "\n")
    .replace(/=0D/g, "\r")
    .replace(/=\r\n/g, "")
    .replace(/=([0-9A-F]{2})/gi, (_, hex) => {
      return String.fromCharCode(parseInt(hex, 16));
    });

  // Extract the main content and remove quoted replies (plain-text only)
  const lines = content.split(/\r?\n/);
  const result: string[] = [];
  
  for (const line of lines) {
    if (/^On .* wrote:/.test(line.trim()) || 
        line.trim().startsWith(">") || 
        line.trim().match(/^(-{2,}|\*{2,}|_{2,})/)) {
      break;
    }
    result.push(line);
  }

  content = result.join("\n").trim();

  // Wrap plain text in basic HTML structure
  content = content
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');
  content = `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.5;"><p>${content}</p></div>`;

  return content;
}

interface ViewableAttachment {
  id: string;
  filename: string | null;
  content_type: string | null;
  size_bytes: number | null;
  public_url: string;
}

function AttachmentViewer({ attachment, allAttachments, onClose, onAddAsExpense }: {
  attachment: ViewableAttachment;
  allAttachments?: ViewableAttachment[];
  onClose: () => void;
  onAddAsExpense?: (att: ViewableAttachment) => void;
}) {
  const [selectedAttachment, setSelectedAttachment] = useState<ViewableAttachment>(attachment);
  const [loading, setLoading] = useState(true);
  const [downloadProgress, setDownloadProgress] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const list = allAttachments && allAttachments.length > 0 ? allAttachments : [attachment];
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const isPdf = selectedAttachment.content_type?.includes('pdf') || /\.pdf(\?.*)?$/i.test(selectedAttachment.filename || '');
  const isImage = selectedAttachment.content_type?.startsWith('image/') || /\.(png|jpe?g|gif|webp|heic|heif|bmp|svg)(\?.*)?$/i.test(selectedAttachment.filename || '');
  const displayName = selectedAttachment.filename || 'Attachment';
  const fileSize = selectedAttachment.size_bytes ? (
    selectedAttachment.size_bytes > 1024 * 1024
      ? `${(selectedAttachment.size_bytes / (1024 * 1024)).toFixed(1)} MB`
      : `${Math.round(selectedAttachment.size_bytes / 1024)} KB`
  ) : null;

  useEffect(() => {
    requestAnimationFrame(() => setIsOpen(true));
  }, []);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleTransitionEnd = () => {
    if (!isOpen) onClose();
  };

  const handleDownload = async (att: ViewableAttachment = selectedAttachment) => {
    setDownloadProgress(true);
    const name = att.filename || 'Attachment';
    try {
      const response = await fetch(att.public_url);
      const blob = await response.blob();
      const file = new File([blob], name, { type: att.content_type || 'application/octet-stream' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: name });
      } else {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = name;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    } catch (err) {
      if ((err as DOMException)?.name !== 'AbortError') {
        window.open(att.public_url, '_blank');
      }
    } finally {
      setDownloadProgress(false);
    }
  };

  const handleShare = async () => {
    try {
      const response = await fetch(selectedAttachment.public_url);
      const blob = await response.blob();
      const file = new File([blob], displayName, { type: selectedAttachment.content_type || 'application/octet-stream' });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: displayName });
      }
    } catch {
      // user cancelled or not supported
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100200,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)'
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div
        className={`bg-white transform transition-all duration-200 ${
          isMobile
            ? `fixed left-0 right-0 w-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[540px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100210,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(isMobile && { left: 0, right: 0 }),
          ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative flex-shrink-0">
            <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
              Attachments
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {list.length} {list.length === 1 ? 'file' : 'files'}
            </p>
          </div>

          {/* Content */}
          <div
            className="flex-1 overflow-y-auto bg-white flex flex-col min-h-0"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {/* Attachment List */}
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-3 flex-shrink-0`}>
              {list.map((att) => {
                const isImg = att.content_type?.startsWith('image/') || /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(att.filename || '');
                const isPdfItem = att.content_type?.includes('pdf') || /\.pdf(\?.*)?$/i.test(att.filename || '');
                const isSelected = att.id === selectedAttachment.id;
                const size = att.size_bytes ? (
                  att.size_bytes > 1024 * 1024
                    ? `${(att.size_bytes / (1024 * 1024)).toFixed(1)} MB`
                    : `${Math.round(att.size_bytes / 1024)} KB`
                ) : null;
                return (
                  <div
                    key={att.id}
                    className={`bg-white rounded-[12px] border p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-150 cursor-pointer active:scale-[0.98] touch-manipulation ${
                      isSelected
                        ? 'border-blue-500 ring-1 ring-blue-200 bg-blue-50/50'
                        : 'border-gray-200/60 hover:border-gray-300'
                    }`}
                    onClick={() => { setSelectedAttachment(att); setLoading(true); }}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex items-center gap-3">
                      {isImg ? (
                        <img src={att.public_url} alt="" className="h-11 w-11 object-cover rounded-[10px] flex-shrink-0" />
                      ) : (
                        <div className={`h-11 w-11 rounded-[10px] flex items-center justify-center flex-shrink-0 ${isPdfItem ? 'bg-red-50' : 'bg-gray-100'}`}>
                          <FileText className={`h-5 w-5 ${isPdfItem ? 'text-red-500' : 'text-gray-400'}`} />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-semibold text-gray-900 truncate leading-tight">{att.filename || 'Attachment'}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {isPdfItem ? 'PDF Document' : isImg ? 'Image' : 'File'}
                          {size && <span> · {size}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {onAddAsExpense && (
                          <button
                            type="button"
                            className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-full transition-colors"
                            onClick={(e) => { e.stopPropagation(); onAddAsExpense(att); }}
                            title="Add as expense"
                          >
                            <Receipt className="h-4 w-4" />
                          </button>
                        )}
                        {typeof navigator.share === 'function' && (
                          <button
                            type="button"
                            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                            onClick={(e) => { e.stopPropagation(); handleShare(); }}
                            title="Share"
                          >
                            <Share2 className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                          onClick={(e) => { e.stopPropagation(); handleDownload(att); }}
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Preview Section */}
            <div className={`${isMobile ? 'px-4 pb-4' : 'px-6 pb-6'} flex-1 min-h-0`}>
              <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden flex flex-col" style={{ minHeight: '300px' }}>
                {/* Preview header */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-gray-900 truncate">{displayName}</div>
                    {fileSize && <div className="text-xs text-gray-500 mt-0.5">{fileSize}</div>}
                  </div>
                </div>

                {/* Preview content */}
                <div className="flex-1 min-h-0 overflow-auto relative bg-gray-50/50">
                  {loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                        <span className="text-sm text-gray-500">Loading {isPdf ? 'PDF' : 'file'}…</span>
                      </div>
                    </div>
                  )}

                  {isPdf ? (
                    <iframe
                      key={selectedAttachment.id}
                      src={`${selectedAttachment.public_url}#toolbar=0`}
                      title={displayName}
                      className="w-full border-0"
                      style={{ minHeight: '500px', height: '100%' }}
                      onLoad={() => setLoading(false)}
                    />
                  ) : isImage ? (
                    <div className="flex items-center justify-center p-6">
                      <img
                        key={selectedAttachment.id}
                        src={selectedAttachment.public_url}
                        alt={displayName}
                        className="max-w-full max-h-[60vh] object-contain rounded-lg"
                        onLoad={() => setLoading(false)}
                        onError={() => setLoading(false)}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 px-6 text-center" ref={() => setLoading(false)}>
                      <div className="w-16 h-16 rounded-2xl bg-gray-200 flex items-center justify-center mb-4">
                        <FileText className="h-8 w-8 text-gray-400" />
                      </div>
                      <div className="text-base font-semibold text-gray-900 mb-1">{displayName}</div>
                      {fileSize && <div className="text-sm text-gray-500 mb-5">{fileSize}</div>}
                      <button
                        type="button"
                        onClick={() => handleDownload()}
                        disabled={downloadProgress}
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl transition-colors touch-manipulation"
                      >
                        {downloadProgress ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        Download File
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Open a URL in the system browser on native, or new tab on web.
 * Used to ensure email links never navigate inside the WKWebView.
 */
async function openExternalUrl(href: string) {
  if (!href || href.startsWith('mailto:') || href.startsWith('tel:')) {
    window.open(href, '_blank');
    return;
  }
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      const { App } = await import('@capacitor/app');
      // This is not the right approach - App doesn't have openUrl for external links
      // Instead, Capacitor can use Browser plugin, but we'll use a simpler approach
    }
  } catch {}
  window.open(href, '_blank');
}

/**
 * Intercept anchor clicks inside a container and open them externally.
 * Attach as onClick on any div that renders HTML email content.
 */
function handleEmailContentClick(e: React.MouseEvent<HTMLElement>) {
  const anchor = (e.target as HTMLElement).closest?.('a');
  if (!anchor) return;
  const href = anchor.getAttribute('href');
  if (!href || href === '#') return;
  e.preventDefault();
  e.stopPropagation();

  // Use dynamic import so it doesn't block rendering
  import('@capacitor/core').then(({ Capacitor }) => {
    if (Capacitor.isNativePlatform()) {
      // On native iOS, use the Browser plugin or fallback to window.open with _system
      import('@capacitor/browser').then(({ Browser }) => {
        Browser.open({ url: href }).catch(() => window.open(href, '_system'));
      }).catch(() => {
        window.open(href, '_system');
      });
    } else {
      window.open(href, '_blank');
    }
  }).catch(() => {
    window.open(href, '_blank');
  });
}

/**
 * After an iframe loads, inject a click interceptor so links open externally
 * instead of navigating inside the WKWebView.
 */
function interceptIframeLinks(iframe: HTMLIFrameElement | null) {
  if (!iframe) return;
  try {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    doc.addEventListener('click', (e: Event) => {
      const anchor = (e.target as HTMLElement).closest?.('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href === '#') return;
      e.preventDefault();
      e.stopPropagation();

      import('@capacitor/core').then(({ Capacitor }) => {
        if (Capacitor.isNativePlatform()) {
          import('@capacitor/browser').then(({ Browser }) => {
            Browser.open({ url: href }).catch(() => window.open(href, '_system'));
          }).catch(() => {
            window.open(href, '_system');
          });
        } else {
          window.open(href, '_blank');
        }
      }).catch(() => {
        window.open(href, '_blank');
      });
    });
  } catch {
    // cross-origin iframe, can't inject
  }
}

export function EmailDisplay({
  content,
  subject,
  createdAt,
  className = "",
  isOutbound = false,
  compactView = false,
  replyTo,
  replySubject,
  businessId,
  conversationId,
  customerId,
  onSendSuccess,
  onSendError,
  isEmailDisabled = false,
  onReplyClick,
  onReplyAllClick,
  onForwardClick,
  replyToGmailId,
  inlineAttachments = [],
  attachments = [],
  threadMessages = [],
  userEmail = null,
  initialOpenFullEmail = false,
  onFullEmailOpened,
  locallyReadMessages,
  onMarkAsRead,
  onBackToUnread
}: EmailDisplayProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const modalIframeRef = useRef<HTMLIFrameElement | null>(null);
  const modalScrollRef = useRef<HTMLDivElement | null>(null);
  const latestEmailRef = useRef<HTMLDivElement | null>(null);
  const [iframeHeight, setIframeHeight] = useState(0);
  const [modalIframeHeight, setModalIframeHeight] = useState(0);
  const [modalIframeLoadGen, setModalIframeLoadGen] = useState(0);
  const [showFullEmail, setShowFullEmail] = useState(false);
  const [showFullThread, setShowFullThread] = useState(false);
  // Resolve from the modal stack so the full-email view sits above whatever modal
  // opened it (e.g. the CRM CustomerMessageModal at a higher z-index). Falls back
  // to the original standalone z-index when not nested.
  const fullEmailZ = useModalZIndex(100065);
  const [viewingAttachment, setViewingAttachment] = useState<ViewableAttachment | null>(null);
  const [expenseAttachment, setExpenseAttachment] = useState<ViewableAttachment | null>(null);
  /** Ignore backdrop closes briefly after programmatic open (card click can land on the new overlay). */
  const ignoreBackdropUntilRef = useRef<number>(0);
  /** Which previous email in the thread is expanded (by id). Latest email is always expanded. */
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);
  /** Message ids whose trailing quoted history is currently revealed (Gmail "•••"). */
  const [quotedShownIds, setQuotedShownIds] = useState<Set<string>>(new Set());
  /** Message ids whose recipient details (to/cc/date) are expanded via the chevron. */
  const [recipientDetailIds, setRecipientDetailIds] = useState<Set<string>>(new Set());
  /** Whether the Gmail-style "N older messages" super-collapsed block is expanded. */
  const [superCollapsedExpanded, setSuperCollapsedExpanded] = useState(false);

  const toggleQuoted = (id: string) =>
    setQuotedShownIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleRecipientDetail = (id: string) =>
    setRecipientDetailIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  useEffect(() => {
    if (!initialOpenFullEmail) return;
    ignoreBackdropUntilRef.current = Date.now() + 400;
    setShowFullEmail(true);
  }, [initialOpenFullEmail]);

  const handleCloseFullEmail = useCallback(() => {
    onFullEmailOpened?.();
    setShowFullEmail(false);
  }, [onFullEmailOpened]);

  /** Close on pointerdown + preventDefault so the gesture can't fall through to the
   *  email bubble underneath (which would immediately reopen the modal). */
  const handleClosePointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    handleCloseFullEmail();
  };

  const handleBackdropPointerDown = (e: React.PointerEvent) => {
    // Only primary button / touch; ignore if still in the post-open grace window
    if (e.button !== 0) return;
    if (Date.now() < ignoreBackdropUntilRef.current) return;
    e.preventDefault();
    handleCloseFullEmail();
  };

  useEffect(() => {
    if (!showFullEmail) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      handleCloseFullEmail();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showFullEmail, handleCloseFullEmail]);

  const escapeHtml = (input: string) => {
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const sanitizeEmailHtml = (input: string) => {
    return DOMPurify.sanitize(input, {
      ALLOWED_TAGS: [
        'html', 'head', 'body', 'meta', 'title', 'style',
        'p', 'br', 'div', 'span', 'strong', 'b', 'em', 'i', 'u', 'a', 'ul', 'ol', 'li',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'table', 'tr', 'td', 'th',
        'thead', 'tbody', 'tfoot', 'img', 'hr'
      ],
      ALLOWED_ATTR: [
        'href', 'target', 'style', 'class', 'src', 'alt', 'width', 'height',
        'align', 'valign', 'colspan', 'rowspan'
      ],
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
    });
  };

  const cidMap = useMemo(() => {
    return inlineAttachments.reduce<Record<string, string>>((acc, attachment) => {
      if (attachment.content_id) {
        acc[attachment.content_id] = attachment.public_url;
      }
      return acc;
    }, {});
  }, [inlineAttachments]);

  const replaceCidSources = (html: string) => {
    if (!html || Object.keys(cidMap).length === 0) return html;
    return html.replace(/cid:([^"'\s>]+)/gi, (match, cid) => {
      const normalized = cid.replace(/[<>]/g, '').trim();
      return cidMap[normalized] ? cidMap[normalized] : match;
    });
  };

  const htmlContent = useMemo(() => {
    const prepared = prepareEmailHTML(content);
    return replaceCidSources(prepared);
  }, [content, cidMap]);
  const looksLikeHtml = useMemo(() => {
    return /<(!doctype|html|head|body|table|style|div|p|span|img|a)\b/i.test(content);
  }, [content]);

  const isFancyHtml = useMemo(() => {
    if (!looksLikeHtml) return false;
    return /<(table|style|img|head|body)\b/i.test(content) || content.length > 8000;
  }, [content, looksLikeHtml]);


  const sanitizedHTML = useMemo(() => {
    return sanitizeEmailHtml(htmlContent);
  }, [htmlContent]);

  const threadHtml = useMemo(() => {
    if (!threadMessages || threadMessages.length === 0) return '';

    const sorted = [...threadMessages].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    const parts = sorted.map((message) => {
      const messageHtml = prepareEmailHTML(message.body);
      let sanitized = sanitizeEmailHtml(messageHtml);
      if (!sanitized.trim()) {
        sanitized = message.body?.trim()
          ? `<pre style="white-space:pre-wrap;font-family:inherit;">${escapeHtml(message.body)}</pre>`
          : `<p style="color:#6b7280;font-style:italic;">Email content could not be loaded.</p>`;
      }

      return `
        <section style="padding:16px 0;border-bottom:1px solid #e5e7eb;">
          <div>${sanitized}</div>
        </section>
      `;
    });

    return parts.join('');
  }, [threadMessages]);

  const iframeDoc = useMemo(() => {
    const extractBodyHtml = (html: string) => {
      try {
        if (typeof window === 'undefined' || !window.DOMParser) return html;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        return doc.body?.innerHTML || html;
      } catch {
        return html;
      }
    };

    const effectiveHtml = sanitizedHTML || sanitizeEmailHtml(content);
    let bodyHtml = extractBodyHtml(effectiveHtml);
    if (!bodyHtml.trim()) {
      bodyHtml = content?.trim()
        ? `<pre style="white-space:pre-wrap;font-family:inherit;">${escapeHtml(content)}</pre>`
        : `<p style="color:#6b7280;font-style:italic;">Email content could not be loaded.</p>`;
    }
    const textColor = '#1f2937';
    const baseStyles = `
      html, body { margin: 0; padding: 0; background: #ffffff; color: ${textColor}; height: auto !important; overflow: hidden !important; }
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; line-height: 1.6; }
      .email-wrapper { width: 100%; margin: 0; padding: 16px; box-sizing: border-box; overflow-x: hidden; }
      img { max-width: 100% !important; height: auto !important; }
      table { max-width: 100% !important; width: auto !important; }
      td, th { word-break: normal; }
    `;

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>${baseStyles}</style>
  </head>
  <body>
    <div class="email-wrapper">
      ${bodyHtml}
    </div>
  </body>
</html>`;
  }, [sanitizedHTML, content, isOutbound, subject, createdAt]);

  const threadDoc = useMemo(() => {
    if (!threadHtml) return '';
    const textColor = '#1f2937';
    const baseStyles = `
      html, body { margin: 0; padding: 0; background: #ffffff; color: ${textColor}; height: auto !important; overflow: hidden !important; }
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; line-height: 1.6; }
      .email-wrapper { width: 100%; margin: 0; padding: 16px; box-sizing: border-box; overflow-x: hidden; }
      img { max-width: 100% !important; height: auto !important; }
      table { max-width: 100% !important; width: auto !important; }
      td, th { word-break: normal; }
    `;

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>${baseStyles}</style>
  </head>
  <body>
    <div class="email-wrapper">${threadHtml}</div>
  </body>
</html>`;
  }, [threadHtml, isOutbound]);

  useEffect(() => {
    if (!looksLikeHtml) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    interceptIframeLinks(iframe);

    const updateHeight = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;
        const newHeight = Math.max(
          doc.body?.scrollHeight || 0,
          doc.documentElement?.scrollHeight || 0
        );
        setIframeHeight(newHeight);
      } catch {
        // ignore cross-origin access errors
      }
    };

    const raf = window.requestAnimationFrame(updateHeight);
    const timer = window.setTimeout(updateHeight, 200);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [iframeDoc, looksLikeHtml]);

  useEffect(() => {
    if (!showFullEmail) return;
    setExpandedThreadId(null);
  }, [showFullEmail]);

  useEffect(() => {
    if (!showFullEmail || modalIframeLoadGen === 0) return;
    return observeIframeHeight(modalIframeRef.current, setModalIframeHeight);
  }, [showFullEmail, modalIframeLoadGen, iframeDoc]);

  /** Thread messages sorted oldest first; last one is the current (latest) email. */
  const sortedThreadMessages = useMemo(() => {
    if (!threadMessages?.length) return [];
    return [...threadMessages].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  }, [threadMessages]);


  /** Sanitized HTML for a thread message body (for expandable older emails).
   *  Unlike the latest email, we keep the full body including quoted replies
   *  since the user explicitly expanded this message to read it. */
  const getSanitizedBodyForMessage = (msg: { body: string }) => {
    if (!msg.body) return '';
    let content = msg.body;

    // Decode common email encodings
    content = content
      .replace(/=3D/g, '=')
      .replace(/=20/g, ' ')
      .replace(/=0A/g, '\n')
      .replace(/=0D/g, '\r')
      .replace(/=\r\n/g, '')
      .replace(/=([0-9A-F]{2})/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));

    const looksLikeHtml = /<(!doctype|html|head|body|table|style|div|p|span|img|a)\b/i.test(content);

    if (!looksLikeHtml) {
      content = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n\n/g, '</p><p>')
        .replace(/\n/g, '<br>');
      content = `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.5;"><p>${content}</p></div>`;
    }

    const sanitized = replaceCidSources(sanitizeEmailHtml(content));
    return sanitized || `<pre style="white-space:pre-wrap;font-family:inherit;">${msg.body.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;
  };

  useEffect(() => {
    if (!showFullEmail) return;
    const scrollContainer = modalScrollRef.current;
    if (!scrollContainer) return;

    const scrollToLatestEmailStart = () => {
      const latest = latestEmailRef.current;
      if (latest) {
        // Align the start of the most recent email with the top of the scroll area
        // (older thread messages sit above and remain reachable by scrolling up).
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const latestTop = latest.getBoundingClientRect().top;
        scrollContainer.scrollTop += latestTop - containerTop;
      } else {
        scrollContainer.scrollTop = 0;
      }
    };

    scrollToLatestEmailStart();
    const raf = window.requestAnimationFrame(scrollToLatestEmailStart);
    // Retries: thread layout / iframes can change height after first paint
    const t1 = window.setTimeout(scrollToLatestEmailStart, 100);
    const t2 = window.setTimeout(scrollToLatestEmailStart, 300);
    const t3 = window.setTimeout(scrollToLatestEmailStart, 700);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [showFullEmail, sortedThreadMessages.length]);

  const bodyPreview = useMemo(() => getBodyPreview(content, 56), [content]);
  const bodyPreviewLong = useMemo(() => getBodyPreview(content, 120), [content]);
  const threadCount = threadMessages?.length ?? 0;
  const participantLabel = isOutbound
    ? (replyTo ? `You, ${shortParticipant(replyTo)}` : 'You')
    : (shortParticipant(replyTo) || 'Them');

  return (
    <div className={`email-display ${className}`}>
      {(compactView || isFancyHtml || looksLikeHtml) ? (
        <div
          className={`block w-full max-w-full rounded-xl border px-3 py-2.5 shadow-sm cursor-pointer transition-colors ${
            isOutbound
              ? 'bg-blue-500 border-blue-600 hover:bg-blue-600'
              : 'bg-white border-gray-200 hover:bg-gray-50'
          }`}
          role="button"
          tabIndex={0}
          onClick={() => setShowFullEmail(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setShowFullEmail(true);
            }
          }}
        >
          {/* Top row: participant, subject, thread count, time, expand button — always one row so expand stays top-right on mobile */}
          <div className="flex items-center gap-1.5 sm:gap-2 w-full min-w-0">
            <div className="flex items-center gap-1.5 min-w-0 flex-1 shrink">
              <span className={`text-xs flex-shrink-0 ${isOutbound ? 'text-blue-100' : 'text-gray-500'}`}>
                {participantLabel}
              </span>
              <span className={`text-xs flex-shrink-0 ${isOutbound ? 'text-blue-200' : 'text-gray-400'}`}>·</span>
              <div className={`text-sm min-w-0 flex-1 truncate ${isOutbound ? 'text-white' : 'text-gray-800'}`}>
                {subject && <span className="font-medium">{subject}</span>}
                {bodyPreview && (
                  <span className={`hidden sm:inline ${subject ? ' opacity-90' : ''}`}>
                    {subject ? ' — ' : ''}{bodyPreview}
                  </span>
                )}
              </div>
            </div>
            {threadCount > 1 && (
              <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded ${isOutbound ? 'bg-blue-600/60 text-blue-100' : 'bg-gray-200 text-gray-600'}`} title={`${threadCount} messages in thread`}>
                {threadCount}
              </span>
            )}
            <button
              className={`inline-flex items-center justify-center h-7 w-7 rounded-full transition-colors flex-shrink-0 ${
                isOutbound
                  ? 'text-blue-100 hover:text-white bg-blue-600/50 hover:bg-blue-600/70'
                  : 'text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setShowFullEmail(true);
              }}
              aria-label="Open full email"
              title="Open full email"
            >
              ⤢
            </button>
          </div>
          {/* Mobile: snippet below top row (longer preview, 2 lines) */}
          {bodyPreviewLong && (
            <div className={`text-xs min-w-0 w-full mt-1 sm:hidden ${isOutbound ? 'text-blue-100/90' : 'text-gray-500'} line-clamp-2`}>
              {bodyPreviewLong}
            </div>
          )}
          {showFullEmail && createPortal(
            <div
              className="fixed inset-0 flex flex-col sm:flex-row sm:justify-end"
              style={{ zIndex: fullEmailZ }}
              role="dialog"
              aria-modal="true"
              aria-label={subject || 'Email'}
            >
              {/* Dedicated backdrop — sibling of the panel so outside clicks always hit this layer */}
              <div
                className="absolute inset-0 bg-black/60"
                onPointerDown={handleBackdropPointerDown}
                aria-hidden="true"
              />
              <div
                className="relative z-10 flex-1 flex flex-col min-h-0 w-full sm:flex-initial sm:w-[560px] sm:max-w-[70vw] sm:h-full bg-white sm:shadow-2xl sm:animate-[slideInRight_0.25s_ease-out]"
              >
                {/* Header */}
                <div
                  className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0"
                  style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 1rem))' }}
                >
                  {/* Mobile back arrow */}
                  <button
                    type="button"
                    className="sm:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
                    onPointerDown={(e) => {
                      if (e.button !== 0) return;
                      handleClosePointerDown(e);
                      onBackToUnread?.();
                    }}
                    aria-label="Back to inbox"
                    style={{ minHeight: '40px', minWidth: '40px' }}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <Mail className="hidden sm:block h-5 w-5 text-gray-400 flex-shrink-0" />
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                      {subject || 'Email'}
                    </h3>
                  </div>
                  <button
                    type="button"
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors touch-manipulation flex-shrink-0"
                    onPointerDown={handleClosePointerDown}
                    aria-label="Close"
                    style={{ minHeight: '40px', minWidth: '40px' }}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div
                  ref={modalScrollRef}
                  className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-y-contain ios-momentum-scroll pb-4 px-4 sm:px-6 pt-4"
                  style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
                >
                  {/* Gmail-style thread: older emails as clickable snippets, latest full */}
                  {sortedThreadMessages.length > 0 ? (
                    <div className="space-y-0">
                      {(() => {
                        const total = sortedThreadMessages.length;
                        const useSuper = total > 4 && !superCollapsedExpanded;
                        const hiddenLo = 1;
                        const hiddenHi = total - 3; // inclusive — keep first + last two visible
                        const hiddenCount = useSuper ? hiddenHi - hiddenLo + 1 : 0;

                        const renderMessage = (msg: typeof sortedThreadMessages[number], index: number) => {
                          const isLatest = index === total - 1;
                          const isExpanded = isLatest || expandedThreadId === msg.id;
                          const isOutboundMsg = msg.direction === 'outbound';
                          const msgTime = formatMessageTime(msg.created_at);
                          const preview = getBodyPreview(msg.body, 90);
                          const sender = isOutboundMsg
                            ? { name: 'You', email: userEmail || '' }
                            : parseEmailAddress(msg.from_number || replyTo);
                          const toList = parseAddressList(msg.to_number);
                          const ccList = parseAddressList(msg.cc);
                          const toLabel = toList.length
                            ? toList.map((a) => a.name || a.email).join(', ')
                            : (isOutboundMsg ? (replyTo ? parseEmailAddress(replyTo).name : '') : 'me');
                          const isMsgUnread = !isOutboundMsg && msg.read === 'unread' && !locallyReadMessages?.has(msg.id);
                          const detailOpen = recipientDetailIds.has(msg.id);
                          const sanitizedHtml = getSanitizedBodyForMessage(msg);
                          const isFancy = /<(table|style|head|body)\b/i.test(msg.body || '') || (msg.body?.length || 0) > 8000;

                          const header = (
                            <div className="flex items-start gap-3">
                              <div className="relative flex-shrink-0">
                                <SenderAvatar email={sender.email} name={sender.name} isOutbound={isOutboundMsg} />
                                {isMsgUnread && (
                                  <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-blue-500 rounded-full border-2 border-white" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                  <span className={`text-sm font-semibold truncate ${isMsgUnread ? 'text-gray-900' : 'text-gray-800'}`}>
                                    {sender.name || sender.email}
                                  </span>
                                  <span className="text-xs text-gray-500 flex-shrink-0">{msgTime}</span>
                                </div>
                                {isExpanded ? (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); toggleRecipientDetail(msg.id); }}
                                    className="flex items-center gap-1 text-xs text-gray-500 mt-0.5 hover:text-gray-700"
                                  >
                                    <span className="truncate max-w-[220px]">to {toLabel || '—'}</span>
                                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${detailOpen ? 'rotate-180' : ''}`} />
                                  </button>
                                ) : (
                                  preview && <div className="text-xs text-gray-500 truncate mt-0.5">{preview}</div>
                                )}
                                {isExpanded && detailOpen && (
                                  <div className="mt-2 mb-1 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-[11px] text-gray-600 space-y-0.5 break-all">
                                    <div><span className="text-gray-400">From: </span>{sender.name ? `${sender.name} <${sender.email}>` : sender.email}</div>
                                    <div><span className="text-gray-400">To: </span>{toList.length ? toList.map((a) => a.email).join(', ') : (isOutboundMsg ? '' : 'me')}</div>
                                    {ccList.length > 0 && (
                                      <div><span className="text-gray-400">Cc: </span>{ccList.map((a) => a.email).join(', ')}</div>
                                    )}
                                    <div><span className="text-gray-400">Date: </span>{new Date(msg.created_at).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );

                          return (
                            <div
                              key={msg.id}
                              ref={isLatest ? latestEmailRef : undefined}
                              className={`border-b ${isLatest ? 'border-gray-200' : 'border-gray-100'}`}
                            >
                              {isExpanded ? (
                                <div className="py-4">{header}</div>
                              ) : (
                                <button
                                  type="button"
                                  className={`w-full text-left py-4 transition-colors rounded ${isMsgUnread ? 'bg-blue-50/40' : 'hover:bg-gray-50/50'}`}
                                  onClick={() => setExpandedThreadId((prev) => (prev === msg.id ? null : msg.id))}
                                >
                                  {header}
                                </button>
                              )}
                              {isExpanded && isMsgUnread && onMarkAsRead && (
                                <div className="flex justify-end px-1 -mt-1 pb-2">
                                  <button
                                    type="button"
                                    className="text-[11px] font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 px-2.5 py-1 rounded-full transition-colors touch-manipulation"
                                    onClick={(e) => { e.stopPropagation(); onMarkAsRead([msg.id]); }}
                                  >
                                    Mark as read
                                  </button>
                                </div>
                              )}
                              {isExpanded && (
                                <div className="pb-5 pr-1 pt-1 sm:pl-[52px]">
                                  <MessageBody
                                    sanitizedHtml={sanitizedHtml}
                                    isFancy={isFancy}
                                    showQuoted={quotedShownIds.has(msg.id)}
                                    onToggleQuoted={() => toggleQuoted(msg.id)}
                                    interceptLinks={interceptIframeLinks}
                                    onContentClick={handleEmailContentClick}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        };

                        const out: React.ReactNode[] = [];
                        sortedThreadMessages.forEach((msg, index) => {
                          if (useSuper && index >= hiddenLo && index <= hiddenHi) {
                            if (index === hiddenLo) {
                              out.push(
                                <button
                                  key="super-collapsed"
                                  type="button"
                                  onClick={() => setSuperCollapsedExpanded(true)}
                                  className="w-full flex items-center justify-center gap-2 py-2.5 px-3 my-2 text-xs font-medium text-gray-500 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-full border border-gray-200 transition-colors touch-manipulation"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                  {hiddenCount} older message{hiddenCount === 1 ? '' : 's'}
                                </button>
                              );
                            }
                            return;
                          }
                          out.push(renderMessage(msg, index));
                        });
                        return out;
                      })()}
                    </div>
                  ) : (
                    <div className="py-4">
                      <iframe
                        ref={modalIframeRef}
                        title={subject || 'Full email'}
                        className="w-full border-0"
                        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                        scrolling="no"
                        srcDoc={iframeDoc}
                        onLoad={() => {
                          interceptIframeLinks(modalIframeRef.current);
                          setModalIframeLoadGen((n) => n + 1);
                        }}
                        style={{
                          minHeight: 120,
                          height: modalIframeHeight ? `${modalIframeHeight}px` : 'auto',
                          overflow: 'hidden',
                        }}
                      />
                    </div>
                  )}
                  {attachments.length > 0 && (
                    <div className="border-t border-gray-200 px-6 py-4 space-y-3">
                      <div className="text-sm font-semibold text-gray-800">Attachments ({attachments.length})</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {attachments.map((attachment) => {
                          const isImg = attachment.content_type?.startsWith('image/') ||
                            /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(attachment.filename || '');
                          const isPdf = attachment.content_type?.includes('pdf') ||
                            /\.pdf(\?.*)?$/i.test(attachment.filename || '');
                          return (
                            <button
                              key={attachment.id}
                              type="button"
                              className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 hover:bg-gray-50 active:bg-gray-100 transition-colors text-left touch-manipulation w-full"
                              onClick={() => setViewingAttachment(attachment)}
                            >
                              {isImg ? (
                                <img
                                  src={attachment.public_url}
                                  alt={attachment.filename || 'Attachment'}
                                  className="h-12 w-12 object-cover rounded-lg flex-shrink-0"
                                />
                              ) : (
                                <div className={`h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isPdf ? 'bg-red-50' : 'bg-gray-100'}`}>
                                  <FileText className={`h-6 w-6 ${isPdf ? 'text-red-500' : 'text-gray-400'}`} />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-gray-800 truncate">
                                  {attachment.filename || 'Attachment'}
                                </div>
                                <div className="text-xs text-gray-500 flex items-center gap-1.5">
                                  {isPdf ? 'PDF' : isImg ? 'Image' : 'File'}
                                  {attachment.size_bytes ? (
                                    <span>· {attachment.size_bytes > 1024 * 1024
                                      ? `${(attachment.size_bytes / (1024 * 1024)).toFixed(1)} MB`
                                      : `${Math.round(attachment.size_bytes / 1024)} KB`
                                    }</span>
                                  ) : null}
                                </div>
                              </div>
                              <ChevronDown className="h-4 w-4 text-gray-400 -rotate-90 flex-shrink-0" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                {replyTo && businessId && (
                  <div className="border-t border-gray-200 bg-white">
                    <div className="px-4 sm:px-6 py-4 flex items-center gap-2" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))' }}>
                      <button
                        onClick={() => {
                          onReplyClick?.(replyTo, replySubject || subject || '', replyToGmailId ?? undefined);
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-full hover:bg-gray-200 transition-colors"
                      >
                        <Mail className="h-4 w-4" />
                        <span>Reply</span>
                      </button>
                      {onReplyAllClick && (
                        <button
                          onClick={() => {
                            const ourAddrs = new Set<string>();
                            sortedThreadMessages.forEach((m) => {
                              if (m.direction === 'outbound') {
                                const e = parseEmailAddress(m.from_number).email;
                                if (e) ourAddrs.add(e);
                              }
                            });
                            if (userEmail) ourAddrs.add(userEmail.toLowerCase());
                            const latest = sortedThreadMessages[sortedThreadMessages.length - 1];
                            const fromA = parseEmailAddress(latest?.from_number || replyTo);
                            const everyone = [fromA, ...parseAddressList(latest?.to_number), ...parseAddressList(latest?.cc)]
                              .filter((a) => a.email && !ourAddrs.has(a.email));
                            const seen = new Set<string>();
                            const uniq = everyone.filter((a) => (seen.has(a.email) ? false : (seen.add(a.email), true)));
                            const primary = uniq[0]?.email || replyTo;
                            const ccRest = uniq.slice(1).map((a) => a.email).join(', ');
                            onReplyAllClick(primary, ccRest, replySubject || subject || '', replyToGmailId ?? undefined);
                          }}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-full hover:bg-gray-200 transition-colors"
                        >
                          <span>Reply all</span>
                        </button>
                      )}
                      {onForwardClick && (
                        <button
                          onClick={() => {
                            const latest = sortedThreadMessages[sortedThreadMessages.length - 1];
                            const fromEmail = parseEmailAddress(latest?.from_number || replyTo).email;
                            const dateStr = latest?.created_at ? new Date(latest.created_at).toLocaleString() : '';
                            const fwdHeader =
                              `<br><div>---------- Forwarded message ----------</div>` +
                              `<div>From: ${escapeHtml(fromEmail)}</div>` +
                              `<div>Date: ${escapeHtml(dateStr)}</div>` +
                              `<div>Subject: ${escapeHtml(subject || '')}</div>` +
                              `<div>To: ${escapeHtml(latest?.to_number || '')}</div><br>`;
                            const body = latest ? getSanitizedBodyForMessage(latest) : sanitizedHTML;
                            const fwdSubject = `Fwd: ${(subject || '').replace(/^(fwd:|fw:)\s*/i, '')}`.trim();
                            onForwardClick(`<br><br>${fwdHeader}${body}`, fwdSubject);
                          }}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-full hover:bg-gray-200 transition-colors"
                        >
                          <span>Forward</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
        </div>
      ) : (
        <>
          {subject && (
            <div className="text-sm font-medium text-gray-800 mb-2">
              {subject}
            </div>
          )}
          <div 
            className="email-content max-w-none"
            style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#1f2937'
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
            onClick={handleEmailContentClick}
          />
        </>
      )}
      {viewingAttachment && (
        <AttachmentViewer
          attachment={viewingAttachment}
          allAttachments={attachments}
          onClose={() => setViewingAttachment(null)}
          onAddAsExpense={businessId ? (att) => {
            setExpenseAttachment(att);
          } : undefined}
        />
      )}
      {expenseAttachment && businessId && (
        <UploadReceiptModalWrapper
          isOpen={true}
          onClose={() => setExpenseAttachment(null)}
          businessId={businessId}
          initialFileUrl={expenseAttachment.public_url}
          initialFileName={expenseAttachment.filename || 'attachment'}
          initialContentType={expenseAttachment.content_type || undefined}
        />
      )}
    </div>
  );
} 